// Twin of zl-api `packages/features/src/SpeedyFlow/hooks/useSsoPopup.ts`. Change both together.

import { useCallback } from 'preact/hooks';

import { SSO_FAILURE_MESSAGE, SSO_SUCCESS_MESSAGE } from '@/routes/checkout/popup';

import usePopupChannel from './usePopupChannel';

const POPUP_TARGET = 'zl-speedy-auth';

const SSO_MESSAGES = [SSO_SUCCESS_MESSAGE, SSO_FAILURE_MESSAGE] as const;

interface UseSsoPopupOptions {
  onSuccess: () => void;
  onFailure: () => void;
}

/**
 * Runs a social login in a new tab, so the page behind the modal is never torn down.
 *
 * zl-app and the API share an origin, so the callback page is same-origin with its opener and can
 * simply post a message and close itself — no backend change. When the browser blocks the popup
 * there is no way to keep the page alive, so the caller falls back to a same-tab redirect carrying
 * the serialised flow state, the way `ppt` and the checkout redirect params already do.
 *
 * The tab's lifecycle is `usePopupChannel`'s; all that is here is what the two outcomes mean.
 */
const useSsoPopup = ({ onSuccess, onFailure }: UseSsoPopupOptions) => {
  const handleMessage = useCallback(
    (type: string) => {
      if (type === SSO_SUCCESS_MESSAGE) {
        onSuccess();
      } else {
        onFailure();
      }
    },
    [onFailure, onSuccess],
  );

  const { open, stop } = usePopupChannel({
    target: POPUP_TARGET,
    messages: SSO_MESSAGES,
    onMessage: handleMessage,
    // A tab closed by hand says nothing. Treat that as a failure rather than hanging: unlike
    // checkout, nothing was charged, so there is no reason to go looking for a side effect.
    onClosed: onFailure,
  });

  // `stop` is passed on because the channel's own teardown runs on unmount, and the flow that owns
  // this never unmounts — it renders `null` when closed. See the lifecycle effect in `SpeedyFlow`.
  return { open, stop };
};

export default useSsoPopup;
